import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Trophy, Search, ChevronLeft, ChevronRight, Loader2, 
  Trash2, Eye, Shield, Pause, Play, CheckCircle, Home
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import adminApi from "../../lib/adminApi";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../../components/ui/dialog";

const ADMIN_EMAIL = "cpt.serdarakdemir@gmail.com";

export default function AdminLeagues() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leagues, setLeagues] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchLeagues();
  }, [page, statusFilter]);

  const fetchLeagues = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getLeagues({ page, limit: 20, search: search || undefined, status: statusFilter || undefined });
      setLeagues(response.data.leagues);
      setTotal(response.data.total);
      setPages(response.data.pages);
    } catch (error) {
      toast.error("Ligler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLeagues();
  };

  const handleStatusChange = async (leagueId, newStatus) => {
    try {
      await adminApi.updateLeagueStatus(leagueId, newStatus);
      toast.success(`Lig durumu '${newStatus}' olarak güncellendi`);
      fetchLeagues();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async () => {
    if (!selectedLeague) return;
    setActionLoading(true);
    try {
      await adminApi.deleteLeague(selectedLeague.league_id);
      toast.success("Lig silindi");
      setShowDeleteDialog(false);
      fetchLeagues();
    } catch (error) {
      toast.error("İşlem başarısız");
    } finally {
      setActionLoading(false);
    }
  };

  // Check admin access
  if (user?.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Yetkisiz Erişim</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-leagues">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/dashboard" className="p-2 rounded-lg bg-[#4DFFFF]/20 hover:bg-[#4DFFFF]/30 text-[#4DFFFF]" title="Ana Sayfa">
          <Home className="w-5 h-5" />
        </Link>
        <Link to="/admin" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Admin Panel">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-[#C6A87C]" />
          <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            Lig Yönetimi
          </h1>
        </div>
        <span className="ml-auto text-sm text-[#64748B]">{total} lig</span>
      </div>

      {/* Filters */}
      <div className="glass-card p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Lig adı ara..."
                className="pl-9 bg-[#050E2E]/50 border-white/10 text-white"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 rounded-lg bg-[#050E2E]/50 border border-white/10 text-white"
          >
            <option value="">Tüm Durumlar</option>
            <option value="active">Aktif</option>
            <option value="paused">Durdurulmuş</option>
            <option value="completed">Tamamlanmış</option>
          </select>
          <Button type="submit" className="bg-[#4DFFFF] text-[#050E2E]">Ara</Button>
        </form>
      </div>

      {/* Leagues Table */}
      <div className="glass-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0A1635]">
                <tr>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">Lig</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">Oyun</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">Oyuncular</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">Haftalar</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">Oluşturulma</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">Durum</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {leagues.map((league) => (
                  <tr key={league.league_id} className="hover:bg-white/5">
                    <td className="p-4">
                      <p className="text-white font-medium">{league.name}</p>
                      <p className="text-xs text-[#64748B]">{league.league_id.slice(0, 8)}...</p>
                    </td>
                    <td className="p-4 text-sm text-[#B1C1C1]">{league.game_name || league.game_id}</td>
                    <td className="p-4 text-sm text-[#B1C1C1]">{league.players?.length || 0}</td>
                    <td className="p-4 text-sm text-[#B1C1C1]">{league.week_count || 0}</td>
                    <td className="p-4 text-sm text-[#64748B]">
                      {league.created_at ? new Date(league.created_at).toLocaleDateString("tr-TR") : "-"}
                    </td>
                    <td className="p-4">
                      {league.status === "active" && (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">Aktif</span>
                      )}
                      {league.status === "paused" && (
                        <span className="px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400">Durdurulmuş</span>
                      )}
                      {league.status === "completed" && (
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">Tamamlanmış</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/league/${league.league_id}`}
                          className="p-2 rounded-lg hover:bg-white/10 text-[#64748B] hover:text-white"
                          title="Görüntüle"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        {league.status === "active" && (
                          <button
                            onClick={() => handleStatusChange(league.league_id, "paused")}
                            className="p-2 rounded-lg hover:bg-yellow-500/20 text-yellow-400"
                            title="Durdur"
                          >
                            <Pause className="w-4 h-4" />
                          </button>
                        )}
                        {league.status === "paused" && (
                          <button
                            onClick={() => handleStatusChange(league.league_id, "active")}
                            className="p-2 rounded-lg hover:bg-green-500/20 text-green-400"
                            title="Aktifleştir"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                        )}
                        {league.status !== "completed" && (
                          <button
                            onClick={() => handleStatusChange(league.league_id, "completed")}
                            className="p-2 rounded-lg hover:bg-blue-500/20 text-blue-400"
                            title="Tamamla"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => { setSelectedLeague(league); setShowDeleteDialog(true); }}
                          className="p-2 rounded-lg hover:bg-red-500/20 text-red-400"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-white/5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#B1C1C1]">{page} / {pages}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page === pages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#0A1635] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Ligi Sil</DialogTitle>
            <DialogDescription className="text-[#B1C1C1]">
              "{selectedLeague?.name}" ligini ve tüm verilerini silmek üzeresiniz. Bu işlem geri alınamaz!
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>İptal</Button>
            <Button onClick={handleDelete} disabled={actionLoading} className="bg-red-500 text-white">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
